import {
  SvgGiftFill,
  SvgLocationHeartFill,
  SvgMyFill,
  SvgRecommFill,
  SvgShoppingBasket,
} from '@common/icon';
import type { Metadata } from 'next';

import { DEFAULT_META, DONATION_ROUTES } from '@/constants';

/** 상품 리뷰 정렬 타입 */
export const GIFT_REVIEW_SORT_TYPE = {
  RATING_DESC: 'RATING_DESC',
  LATEST: 'LATEST',
} as const;

/** 상품 리뷰 정렬 타입 라벨 */
export const GIFT_REVIEW_SORT_TYPE_LABEL = {
  [GIFT_REVIEW_SORT_TYPE.RATING_DESC]: '평점 높은순',
  [GIFT_REVIEW_SORT_TYPE.LATEST]: '최신등록순',
} as const;

/** 상품 문의 타입 */
export const INQUIRY_TYPE = {
  /** 답례품 문의 */
  GIFT_PRODUCT: 'HOME_INQUIRY_TYPE_GIFT_PRODUCT',
  /** 주문/배송 문의 */
  DELIVERY: 'HOME_INQUIRY_TYPE_DELIVERY',
  /** 기타 */
  ETC: 'HOME_INQUIRY_TYPE_ETC',
} as const;

/** 상품 판매 상태 */
export const GIFT_SALE_STATUS = {
  ON_SALE: 'ON_SALE',
  SOLD_OUT: 'SOLD_OUT',
  TEMPORARILY_UNAVAILABLE: 'TEMPORARILY_UNAVAILABLE',
  STOPPED: 'STOPPED',
} as const;

/** 상품 판매 상태 라벨 */
export const GIFT_SALE_STATUS_LABEL = {
  [GIFT_SALE_STATUS.ON_SALE]: '판매중',
  [GIFT_SALE_STATUS.SOLD_OUT]: '품절',
  [GIFT_SALE_STATUS.TEMPORARILY_UNAVAILABLE]: '일시품절',
  [GIFT_SALE_STATUS.STOPPED]: '판매중지',
} as const;

/** 답례품 정렬 타입 */
export const GIFT_PRODUCT_SORT_TYPE = {
  LATEST: 'LATEST',
  POPULAR: 'POPULAR',
  REVIEW_COUNT: 'REVIEW_COUNT',
} as const;

/** 답례품 정렬 타입 라벨 */
export const GIFT_PRODUCT_SORT_TYPE_LABEL = {
  [GIFT_PRODUCT_SORT_TYPE.LATEST]: '최신등록순',
  [GIFT_PRODUCT_SORT_TYPE.POPULAR]: '인기순',
  [GIFT_PRODUCT_SORT_TYPE.REVIEW_COUNT]: '후기 많은순',
} as const;

/** 주문 상태 */
export const ORDER_STATUS = {
  /** 취소 */
  CANCELLED: 'CANCELLED',
  /** 배송완료 */
  DELIVERY_COMPLETE: 'DELIVERY_COMPLETE',
  /** 배송준비중 */
  DELIVERY_PREPARING: 'DELIVERY_PREPARING',
  /** 교환/반품 */
  EXCHANGE_AND_RETURN: 'EXCHANGE_AND_RETURN',
  /** 배송중 */
  IN_DELIVERY: 'IN_DELIVERY',
  /** 결제완료 */
  PAYMENT_COMPLETE: 'PAYMENT_COMPLETE',
  /** 구매확정 */
  PURCHASE_CONFIRMED: 'PURCHASE_CONFIRMED',
} as const;

/** 주문 상태 라벨 */
export const ORDER_STATUS_LABEL = {
  [ORDER_STATUS.CANCELLED]: '취소',
  [ORDER_STATUS.DELIVERY_COMPLETE]: '배송완료',
  [ORDER_STATUS.DELIVERY_PREPARING]: '배송준비중',
  [ORDER_STATUS.EXCHANGE_AND_RETURN]: '교환/반품',
  [ORDER_STATUS.IN_DELIVERY]: '배송중',
  [ORDER_STATUS.PAYMENT_COMPLETE]: '결제완료',
  [ORDER_STATUS.PURCHASE_CONFIRMED]: '구매확정',
} as const;

/** API 주문 상태 정규화 */
export const normalizeOrderStatus = (
  apiStatus: string,
): keyof typeof ORDER_STATUS_LABEL => {
  const statusMap: Record<string, keyof typeof ORDER_STATUS_LABEL> = {
    GIFT_ORDER_STATUS_CANCELLED: ORDER_STATUS.CANCELLED,
    GIFT_ORDER_STATUS_DELIVERY_COMPLETE: ORDER_STATUS.DELIVERY_COMPLETE,
    GIFT_ORDER_STATUS_DELIVERY_PREPARING: ORDER_STATUS.DELIVERY_PREPARING,
    GIFT_ORDER_STATUS_EXCHANGE_AND_RETURN: ORDER_STATUS.EXCHANGE_AND_RETURN,
    GIFT_ORDER_STATUS_IN_DELIVERY: ORDER_STATUS.IN_DELIVERY,
    GIFT_ORDER_STATUS_PAYMENT_COMPLETE: ORDER_STATUS.PAYMENT_COMPLETE,
    GIFT_ORDER_STATUS_PURCHASE_CONFIRMED: ORDER_STATUS.PURCHASE_CONFIRMED,
  };

  return statusMap[apiStatus] || ORDER_STATUS.CANCELLED;
};

/** 주문 상태별 액션 */
export const ORDER_STATUS_ACTIONS = {
  [ORDER_STATUS.PAYMENT_COMPLETE]: [
    // { action: 'cancel', label: '주문취소' },
  ],
  [ORDER_STATUS.DELIVERY_PREPARING]: [
    // { action: 'cancel', label: '주문취소' },
  ],
  [ORDER_STATUS.IN_DELIVERY]: [{ action: 'delivery', label: '배송조회' }],
  [ORDER_STATUS.DELIVERY_COMPLETE]: [
    { action: 'delivery', label: '배송조회' },
    { action: 'review', label: '후기작성' },
    { action: 'confirm', label: '구매확정' },
    // { action: 'return-exchange', label: '반품/교환' },
  ],
  [ORDER_STATUS.PURCHASE_CONFIRMED]: [{ action: 'review', label: '후기작성' }],
  [ORDER_STATUS.CANCELLED]: [],
  [ORDER_STATUS.EXCHANGE_AND_RETURN]: [],
} as const;

/** 지자체별 고유 코드 (모든 환경에서 동일) */
export const GOV_CODE = {
  /** 제주도 */
  JEJU: '50000',
  /** 광주 - 서구 */
  GWANGJU_SEOGOU: '29140',
  /** 광주 - 남구 */
  GWANGJU_NAMGOU: '29155',
  /** 광주 - 북구 */
  GWANGJU_BUKGOU: '29170',
} as const;

/** 지자체 지정기부사업별 고유 아이디 (모든 환경에서 동일) */
export const HOMETOWN_BUSINESS_ID = {
  /** 광주 - 서구 */
  GWANGJU_SEOGOU: 2145,
  /** 광주 - 남구 */
  GWANGJU_NAMGOU: 2143,
  /** 광주 - 북구 */
  GWANGJU_BUKGOU: 2144,
} as const;

export const SPECIFIC_DONATION_STATUS = {
  /** 기부 진행중 */
  IN_PROGRESS: 'IN_PROGRESS',
  /** 기부 종료 */
  ENDED: 'ENDED',
} as const;

export const DONATION_DEFAULT_META = {
  ...DEFAULT_META,
  title: '고향사랑기부 | 웰로',
} as const satisfies Metadata;

export const DONATION_BNB_TOAST_ITEMS = [
  {
    label: '홈',
    route: DONATION_ROUTES.HOME,
    icon: SvgRecommFill,
  },
  {
    label: '지정기부',
    route: DONATION_ROUTES.HOMETOWNS_BUSINESS,
    icon: SvgLocationHeartFill,
  },
  {
    label: '답례품',
    route: DONATION_ROUTES.GIFTS,
    icon: SvgGiftFill,
  },
  {
    label: '장바구니',
    route: DONATION_ROUTES.CART,
    icon: SvgShoppingBasket,
  },
  {
    label: '마이페이지',
    route: DONATION_ROUTES.MY,
    icon: SvgMyFill,
  },
] as const;

export const VIEW_CONTENTS_TYPE = {
  GIFT_PRODUCT: 'GIFT_PRODUCT',
  INSTITUTION_BUSINESS: 'INSTITUTION_BUSINESS',
} as const;

export const EVENT_TYPE = {
  DONATION: 'DONATION',
  JEJU_8M_EVENT: 'JEJU_8M_EVENT',
  ORDER_PRODUCT_REVIEW: 'ORDER_PRODUCT_REVIEW',
  INVITE_EVENT: 'INVITE_EVENT',
  INVITEE_ON_DONATION: 'INVITEE_ON_DONATION',
  INVITEE_ON_JOIN: 'INVITEE_ON_JOIN',
  INVITER_ON_DONATION: 'INVITER_ON_DONATION',
  INVITER_ON_JOIN: 'INVITER_ON_JOIN',
} as const;

export const NOTICE_CATEGORY_TYPE = {
  NOTICE: 'NOTICE',
  WINNER_ANNOUNCEMENT: 'WINNER_ANNOUNCEMENT',
} as const;

export const INQUIRY_TYPE_LABEL = {
  [INQUIRY_TYPE.GIFT_PRODUCT]: {
    label: '답례품 문의',
    description: '답례품 구성, 품절, 변경 관련 문의',
  },
  [INQUIRY_TYPE.DELIVERY]: {
    label: '주문/배송 문의',
    description: '주문 내역, 결제, 배송 관련 문의',
  },
  [INQUIRY_TYPE.ETC]: {
    label: '기타',
    description: '기타 문의 사항',
  },
} as const;
